// CHANGE v2 §14.6: modal pós-selfie para confirmar/saltar contribuições mensais.
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '../ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/Dialog';
import { MoneyDisplay } from '../common/MoneyDisplay';
import { snapshotsAPI } from '../../data/snapshots';
import { potsAPI } from '../../data/pots';
import pt from '../../locales/pt';

export function SelfieMonthlyCheckModal({
  snapshotId,
  year,
  month,
  isOpen,
  onClose,
}) {
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen || !snapshotId) {
      setItems([]);
      return;
    }
    setIndex(0);
    setItems([]);
    snapshotsAPI
      .monthlyChecks(snapshotId)
      .then((response) => {
        if (response.success) {
          const onlyNeeded = (response.data || []).filter((row) => row.needs_check);
          setItems(onlyNeeded);
          if (onlyNeeded.length === 0) {
            onClose();
          }
        } else {
          onClose();
        }
      });
  }, [isOpen, snapshotId, onClose]);

  const close = () => {
    onClose();
  };

  if (!isOpen || items.length === 0) return null;

  const current = items[index];
  const isLast = index >= items.length - 1;

  const advanceOrFinish = () => {
    if (isLast) {
      close();
    } else {
      setIndex((prev) => prev + 1);
    }
  };

  const handleConfirm = async () => {
    setBusy(true);
    const response = await potsAPI.confirmMonthlyContribution(current.pot_id, {
      action: 'confirmed',
      year,
      month,
    });
    setBusy(false);
    if (response.success) {
      toast.success(pt.snapshots.monthlyCheck.confirmedToast);
      advanceOrFinish();
    }
  };

  const handleSkip = async () => {
    setBusy(true);
    const response = await potsAPI.confirmMonthlyContribution(current.pot_id, {
      action: 'skipped',
      year,
      month,
    });
    setBusy(false);
    if (response.success) {
      toast.info?.(pt.snapshots.monthlyCheck.skippedToast) ??
        toast(pt.snapshots.monthlyCheck.skippedToast);
      advanceOrFinish();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && close()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{pt.snapshots.monthlyCheck.title}</DialogTitle>
          <DialogDescription>
            {pt.snapshots.monthlyCheck.subtitle}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 rounded-xl border border-border bg-background p-4">
          <p className="font-semibold text-foreground">{current.name}</p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground">
                {pt.snapshots.monthlyCheck.expectedLabel}
              </span>
              <MoneyDisplay value={Number(current.expected)} className="font-semibold" />
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground">
                {pt.snapshots.monthlyCheck.contributedLabel}
              </span>
              <MoneyDisplay
                value={Number(current.contributed)}
                className="font-semibold"
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" onClick={handleSkip} disabled={busy}>
            {pt.snapshots.monthlyCheck.skip}
          </Button>
          <Button onClick={handleConfirm} disabled={busy}>
            {pt.snapshots.monthlyCheck.confirm}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          {index + 1} / {items.length}
        </p>
      </DialogContent>
    </Dialog>
  );
}
